import React, { useState, useEffect, useRef } from 'react';
import authoringService from '../api';
import WidgetHeader from './WidgetHeader';
import WidgetContent from './WidgetContent';

const AuthorWidgetComponent = ({
  id,
  title: initialTitle,
  prompt,
  chartQuery,
  onDelete,
  onTitleChange,
  onContentChange
}) => {
  const [title, setTitle] = useState(initialTitle);
  const [ragPrompt, setRagPrompt] = useState(prompt || "");
  const [chartPrompt, setChartPrompt] = useState(chartQuery || "");
  const [tempPrompt, setTempPrompt] = useState(ragPrompt);
  const [tempChartPrompt, setTempChartPrompt] = useState(chartPrompt);
  const [showEditor, setShowEditor] = useState(false);
  const [chartHTML, setChartHTML] = useState(null);
  const [ragResult, setRagResult] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isVisualization, setIsVisualization] = useState(!!chartQuery);
  
  const contentFetchedRef = useRef(false);

  const fetchData = async () => {
    if (contentFetchedRef.current) return;
    
    setLoading(true);
    try {
      const data = await authoringService.generateContent(ragPrompt);
      const formattedText = formatText(data.response);
      setRagResult(formattedText);
      
      if (typeof onContentChange === "function") {
        onContentChange(id, formattedText);
      }
      
      setLoading(false);
      contentFetchedRef.current = true;

      if (isVisualization && chartPrompt) {
        let jsonData = data.response;
        
        if (typeof jsonData === "string") {
          try {
            // Try to extract JSON from the response if it's a string
            const jsonMatch = jsonData.match(/(\[.*\]|\{.*\})/s);
            if (jsonMatch) {
              jsonData = JSON.parse(jsonMatch[0]);
            } else {
              jsonData = JSON.parse(jsonData);
            }
          } catch (e) {
            console.error("Failed to parse response:", e);
            jsonData = [];
          }
        }

        // Format data for chart visualization
        let parsedJson;
        if (Array.isArray(jsonData)) {
          if (jsonData.length > 0 && Array.isArray(jsonData[0])) {
            // Handle array of arrays format
            parsedJson = jsonData.map(([key, value]) => ({
              key,
              value
            }));
          } else {
            // Handle regular array format
            parsedJson = jsonData;
          }
        } else if (typeof jsonData === 'object' && jsonData !== null) {
          // Handle object format
          parsedJson = Object.entries(jsonData).map(([key, value]) => ({
            key,
            value
          }));
        } else {
          parsedJson = [];
        }

        const html = await authoringService.generateChart(parsedJson, chartPrompt);
        const blob = new Blob([html], { type: "text/html" });
        setChartHTML(URL.createObjectURL(blob));
      }
    } catch (err) {
      console.error("Error in widget fetch:", err);
      setRagResult("Failed to load content. Please try again.");
      setLoading(false);
      contentFetchedRef.current = true;
    }
  };

  useEffect(() => {
    if (ragPrompt) {
      fetchData();
    } else {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    // Reset fetch tracker when prompt changes
    if (ragPrompt !== prompt || chartPrompt !== chartQuery) {
      contentFetchedRef.current = false;
      fetchData();
    }
  }, [ragPrompt, chartPrompt]);

  const handleEditorToggle = () => {
    setShowEditor(!showEditor);
    if (!showEditor) {
      setTempPrompt(ragPrompt);
      setTempChartPrompt(chartPrompt);
    }
  };

  const handleCancel = () => {
    setShowEditor(false);
    setTempPrompt(ragPrompt);
    setTempChartPrompt(chartPrompt);
  };

  const handleSave = () => {
    setShowEditor(false);
    
    const promptChanged = ragPrompt !== tempPrompt;
    const chartChanged = chartPrompt !== tempChartPrompt;
    
    if (promptChanged || chartChanged) {
      setRagPrompt(tempPrompt);
      setChartPrompt(tempChartPrompt);
      setIsVisualization(!!tempChartPrompt);
      contentFetchedRef.current = false;
    }
    
    if (title !== initialTitle && typeof onTitleChange === "function") {
      onTitleChange(id, title);
    }
  };

  const handleTitleChange = (e) => {
    setTitle(e.target.value);
  };

  const formatText = (text) => {
    if (!text) return "";
    if (Array.isArray(text)) {
      return text.map(item => Array.isArray(item) ? item.join(": ") : item).join(", ");
    }
    
    return text
      .replace(/^\s*###\s*/gm, "")
      .replace(/^\s*####\s*/gm, "")
      .replace(/\*\*(.*?)\*\*/g, "<strong>$1</strong>")
      .replace(/\n/g, "<br>")
      .trim();
  };

  return (
    <div className="bg-white rounded-lg shadow-md overflow-hidden">
      {/* Widget Ribbon */}
      <WidgetHeader 
        showEditor={showEditor}
        tempPrompt={tempPrompt}
        setTempPrompt={setTempPrompt}
        isVisualization={isVisualization}
        tempChartPrompt={tempChartPrompt}
        setTempChartPrompt={setTempChartPrompt}
        ragPrompt={ragPrompt}
        handleSave={handleSave}
        handleCancel={handleCancel}
        handleEditorToggle={handleEditorToggle}
        onDelete={onDelete}
        id={id}
      />
      
      {/* Widget Content */}
      <div className="p-4">
        {/* Title */}
        <div className="mb-4">
          {showEditor ? (
            <input
              type="text"
              value={title}
              onChange={handleTitleChange}
              className="w-full px-3 py-2 text-lg font-semibold border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500/30"
            />
          ) : (
            <h3 className="text-lg font-semibold text-gray-800">{title}</h3>
          )}
        </div>
        
        {/* Content */}
        <div className="min-h-[120px]">
          <WidgetContent 
            loading={loading}
            showEditor={showEditor}
            isVisualization={isVisualization}
            ragResult={ragResult}
            chartHTML={chartHTML}
            onContentChange={onContentChange}
            id={id}
            title={title}
          />
        </div>
      </div>
    </div>
  );
};

export default AuthorWidgetComponent;